import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';

const formatDate = (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '-');
const formatKm = (value) => (value === null || value === undefined ? '-' : `${Number(value).toLocaleString('pt-BR')} km`);

const plural = (n, singular, pluralText) => (Math.abs(n) === 1 ? singular : pluralText);

const describeDate = (item) => {
    const days = item.days_remaining;
    if (days < 0) {
        const late = Math.abs(days);
        return `Prazo: ${formatDate(item.next_date)} (${late} ${plural(late, 'dia', 'dias')} de atraso)`;
    }
    if (days === 0) return `Prazo: ${formatDate(item.next_date)} (vence hoje)`;
    return `Prazo: ${formatDate(item.next_date)} (faltam ${days} ${plural(days, 'dia', 'dias')})`;
};

const describeKm = (item) => {
    const remaining = item.km_remaining;
    if (remaining < 0) return `KM alvo: ${formatKm(item.target_kilometer)} (${formatKm(Math.abs(remaining))} acima)`;
    return `KM alvo: ${formatKm(item.target_kilometer)} (atingido)`;
};

/**
 * Modal com os serviços recorrentes que precisam ser refeitos no veículo
 * selecionado na geração da OS. `data` é a resposta de
 * GET /vehicles/{id}/recurring-services/due.
 */
export default function RecurringServicesDialog({ open, onClose, onAccept, data }) {
    const items = useMemo(() => data?.items || [], [data]);
    const [selected, setSelected] = useState(() => new Set());

    // Ao abrir (ou receber nova consulta), pré-marca os serviços vencidos.
    useEffect(() => {
        if (!open) return;
        setSelected(new Set(items.filter((item) => item.status === 'vencido').map((item) => item.service_id)));
    }, [open, items]);

    const toggle = (serviceId) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(serviceId)) next.delete(serviceId);
            else next.add(serviceId);
            return next;
        });
    };

    const handleAccept = () => {
        onAccept?.(items.filter((item) => selected.has(item.service_id)));
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="recurring-services-title">
            <DialogTitle id="recurring-services-title">
                Serviços a refazer neste veículo
                {data?.current_kilometer > 0 && (
                    <Typography variant="body2" color="text.secondary">
                        KM atual do veículo: {formatKm(data.current_kilometer)}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={1.5}>
                    {items.map((item) => (
                        <Box
                            key={item.service_id}
                            sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}
                        >
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            size="small"
                                            checked={selected.has(item.service_id)}
                                            onChange={() => toggle(item.service_id)}
                                        />
                                    )}
                                    label={<Typography variant="subtitle2">{item.service_name}</Typography>}
                                />
                                <Chip
                                    size="small"
                                    label={item.status === 'vencido' ? 'Vencido' : 'A vencer'}
                                    color={item.status === 'vencido' ? 'error' : 'warning'}
                                />
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                                Última vez: {formatDate(item.last_date)}
                                {item.last_order_number > 0 && ` (OS ${item.last_order_number})`}
                                {item.last_kilometer !== null && item.last_kilometer !== undefined && ` - ${formatKm(item.last_kilometer)}`}
                            </Typography>
                            {item.criteria.includes('DATE') && (
                                <Typography variant="body2">{describeDate(item)}</Typography>
                            )}
                            {item.criteria.includes('KM') && (
                                <Typography variant="body2">{describeKm(item)}</Typography>
                            )}
                        </Box>
                    ))}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined">Continuar</Button>
                <Button onClick={handleAccept} variant="contained" disabled={selected.size === 0}>
                    Adicionar à OS
                </Button>
            </DialogActions>
        </Dialog>
    );
}
